import * as R from 'ramda';
import {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react';
// constants
import {
  FIELD_DATE,
  FIELD_NAME,
  FIELD_TYPE,
  FIELD_VALUE,
} from '../../../../constants';
// utilities
import * as U from '../../../../utilities';
// feature report-format-panel
import { setEditReportFormProps } from '../field-list/hocs';
import { rangeObject, defaultFilterFields } from './settings';
import { getAvailableFilterFields, getAvailableForReportFilter } from '../../../report-format-panel/helpers';
import { REPORT_FILTER_DATE_LAST_NEXT_OPERATIONS } from '../../../report-format-panel/settings/filter-settings';
//////////////////////////////////////////////////

const withReportFilterList = WrappedComponent => props => { // eslint-disable-line
  const defaultGroupId = 0;
  const updatedProps = useMemo(() => setEditReportFormProps(props), [props]);

  const { filterFields } = updatedProps;

  const { reportFilterGroups, setReportFilterGroups } = props;

  const availableForFilterFields = getAvailableFilterFields(props);

  const [reportSimpleFiltersGroupId, setReportSimpleFiltersGroupId] = useState(defaultGroupId);
  const [availableForFilter, setAvailableForFilter] = useState(R.or(getAvailableForReportFilter(filterFields, availableForFilterFields), []));

  useEffect(() => {
    setReportSimpleFiltersGroupId(defaultGroupId);

    if (U.isNilOrEmpty(filterFields)) return;

    const groups = R.compose(
      R.groupBy(R.prop('groupId')),
      R.map(item => {
        const groupId = R.prop('groupId', item);

        if (U.isNilOrEmpty(groupId)) {
          return {
            ...item,
            withoutOrGroup: true,
            groupId: defaultGroupId,
          };
        }

        return item;
      }),
    )(filterFields);

    setReportFilterGroups(groups);
  }, []); //eslint-disable-line

  const handleAddFiltersGroup = useCallback(() => {
    const groupId = reportSimpleFiltersGroupId;
    const filter = {
      ...defaultFilterFields,
      groupId,
      withoutOrGroup: true,
    };
    const newGroups = R.assoc(groupId, R.of(Array, filter), reportFilterGroups);

    setReportFilterGroups(newGroups);
    setAvailableForFilter(getAvailableForReportFilter(newGroups, availableForFilterFields));
  }, [reportFilterGroups, reportSimpleFiltersGroupId, setReportFilterGroups, availableForFilterFields]);

  const handleAddReportGroup = useCallback(() => {
    const groupId = U.generateUuid();
    const filter = R.assoc('groupId', groupId, defaultFilterFields);
    const newGroups = R.assoc(groupId, R.of(Array, filter), reportFilterGroups);

    setReportFilterGroups(newGroups);
    setAvailableForFilter(getAvailableForReportFilter(newGroups, availableForFilterFields));
  }, [reportFilterGroups, setReportFilterGroups, availableForFilterFields]);

  const handleAddReportFilter = useCallback(({ groupId, withoutOrGroup }) => {
    const group = R.path([groupId], reportFilterGroups);
    const filter = {
      ...defaultFilterFields,
      groupId,
      withoutOrGroup,
    };
    const newGroups = R.assoc(groupId, R.append(filter, group), reportFilterGroups);

    setReportFilterGroups(newGroups);
    setAvailableForFilter(getAvailableForReportFilter(R.head(newGroups), availableForFilterFields));
  }, [reportFilterGroups, setReportFilterGroups, availableForFilterFields]);

  const handleRemoveReportFilter = useCallback((groupId, filterIndex) => {
    const groupsUpdated = R.dissocPath([groupId, filterIndex], reportFilterGroups);
    const group = R.path([groupId], groupsUpdated);

    if (U.isNilOrEmpty(group)) {
      return setReportFilterGroups(R.dissoc(groupId, reportFilterGroups));
    }

    return setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleSelectPropertyName = useCallback((values, filterIndex, dataType) => {
    const name = R.pathOr('', [FIELD_NAME], values);
    const type = R.pathOr('', [FIELD_TYPE], values);

    const newFilter = {
      ...defaultFilterFields,
      propertyName: name,
      withoutOrGroup: true,
      groupId: defaultGroupId,
      dataType: R.or(type, dataType),
    };

    const groupsUpdated = R.assocPath([defaultGroupId, filterIndex], newFilter, reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
    setAvailableForFilter(getAvailableForReportFilter(R.head(groupsUpdated), availableForFilterFields));
  }, [reportFilterGroups, setReportFilterGroups, availableForFilterFields]);

  const handleSelectOperation = useCallback((values, filterIndex, dataType) => {
    const value = R.pathOr('', [FIELD_VALUE], values);

    const groupsUpdated = R.assocPath([defaultGroupId, filterIndex, 'operation'], value, reportFilterGroups);

    if (R.equals(dataType, FIELD_DATE)) {
      const dateRelative = R.includes(value, REPORT_FILTER_DATE_LAST_NEXT_OPERATIONS);

      if (U.isTrue(dateRelative)) {
        const groups = R.compose(
          R.assocPath([defaultGroupId, filterIndex, 'timeUnit'], value),
          R.assocPath([defaultGroupId, filterIndex, 'operation'], 'time_unit'),
          R.assocPath([defaultGroupId, filterIndex, 'dateRelative'], dateRelative),
        )(groupsUpdated);

        return setReportFilterGroups(groups);
      }

      return setReportFilterGroups(R.assocPath([defaultGroupId, filterIndex, 'dateRelative'], dateRelative, groupsUpdated));
    }

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleChangeSelect = useCallback(({
    values,
    groupId,
    filterProp,
    filterIndex,
  }) => {
    let name = R.pathOr('', [FIELD_NAME], values);
    const value = R.pathOr('', [FIELD_VALUE], values);

    if (R.equals(filterProp, 'booleanValue')) {
      if (U.isTrue(value)) {
        name = U.getTranslation('labels.yes', 'Yes');
      }

      if (U.isFalse(value)) {
        name = U.getTranslation('labels.no', 'No');
      }
    }

    const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], R.or(name, value), reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleChangeInput = useCallback(({
    value,
    groupId,
    filterProp,
    filterIndex,
  }) => {
    const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], value, reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleChangeMultiSelect = useCallback(({ options, groupId, filterIndex }) => {
    const groupsUpdated = R.assocPath([groupId, filterIndex, 'selectMultipleValue'], options, reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleChangeRange = useCallback(({
    value,
    groupId,
    rangeType,
    filterProp,
    filterIndex,
  }) => {
    const filterRange = R.path([groupId, filterIndex, filterProp], reportFilterGroups);
    let rangeUpdated = U.ifElse(
      U.isNilOrEmpty(filterRange),
      R.assoc(rangeType, value, rangeObject),
      R.assoc(rangeType, value, R.or(filterRange, {})),
    );

    if (R.all(U.isNilOrEmpty, R.values(rangeUpdated))) rangeUpdated = null;

    const groupsUpdated = R.assocPath([groupId, filterIndex, filterProp], rangeUpdated, reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  const handleChangeDate = useCallback(({
    value,
    groupId,
    rangeType,
    filterProp,
    filterIndex,
  }) => {
    if (R.equals(filterProp, 'dateRelative')) {
      const groupsUpdated = R.assocPath(
        [groupId, filterIndex, rangeType],
        U.ifElse(R.isEmpty(value), null, U.toNumber(value)),
        reportFilterGroups,
      );

      return setReportFilterGroups(groupsUpdated);
    }

    const valueToUse = U.ifElse(
      R.equals(value, 'Invalid date'),
      null,
      value,
    );
    const groupsUpdated = R.assocPath([groupId, filterIndex, rangeType], valueToUse, reportFilterGroups);

    setReportFilterGroups(groupsUpdated);
  }, [reportFilterGroups, setReportFilterGroups]);

  return (
    <WrappedComponent
      {...props}
      handleChangeDate={handleChangeDate}
      availableFields={availableForFilter}
      handleChangeInput={handleChangeInput}
      handleChangeRange={handleChangeRange}
      handleChangeSelect={handleChangeSelect}
      reportFilterGroups={reportFilterGroups}
      handleAddReportGroup={handleAddReportGroup}
      handleSelectOperation={handleSelectOperation}
      handleAddFiltersGroup={handleAddFiltersGroup}
      handleAddReportFilter={handleAddReportFilter}
      setReportFilterGroups={setReportFilterGroups}
      handleChangeMultiSelect={handleChangeMultiSelect}
      handleRemoveReportFilter={handleRemoveReportFilter}
      handleSelectPropertyName={handleSelectPropertyName}
      reportSimpleFiltersGroupId={reportSimpleFiltersGroupId}
      setReportSimpleFiltersGroupId={setReportSimpleFiltersGroupId}
    />
  );
};

export {
  withReportFilterList,
};
